import { useState } from 'react'
import type { FC } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Box, Grid, Button, Divider, TextField, CardContent, Typography } from '@mui/material'
import { EditField, PhoneNumberField, isIncompletePhoneNumber } from '~/shared/ui'
import { useFormik } from 'formik'
import { InputAdornment } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Card from '@mui/material/Card'
import {
  type StatesListResponse,
  referralSteerage,
  referralSteerageProviders,
} from '@fireflyhealth/api'
import {
  ReasonForException,
  Steerage,
  SteerageProvider,
  SteerageProviderDataSource,
  useAddProviderToSteerage,
  useEditSteerageSteerageProvider,
  type EditSteerageProviderPayload,
} from '~/entities/steerage'
import { fireAndForget } from '~/shared/lib/fireAndForget'

interface SteerageProps {
  steerage: Steerage
  steerageProviderCreateForm: boolean
  steerageProvider: SteerageProvider | null
  /** Closes the form. Never given a meaningful argument — every caller just hides the form. */
  onCancel: () => void
  reasonForExceptionList: string[][] | undefined
  states: StatesListResponse
}

const SteerageProviderForm: FC<SteerageProps> = props => {
  const states = props?.states
  const client = useQueryClient()

  const [stateSearchString, setStateSearchString] = useState<string>('')
  const [selectedState, setSelectedState] = useState<StatesListResponse[number] | null>(
    props?.steerageProvider?.state ?? null,
  )

  const { mutate: handleEditSteerageProvider } = useEditSteerageSteerageProvider()
  const { mutate: handleAddProviderToSteerage } = useAddProviderToSteerage()

  const setSearchTermsForState = (
    event: React.SyntheticEvent,
    newValue: StatesListResponse[number] | null,
  ) => {
    setSelectedState(newValue)
  }
  const [phoneValue, setPhoneValue] = useState<string | null>(
    props?.steerageProvider?.phone?.toString() ?? null,
  )

  const [faxValue, setFaxValue] = useState<string | null>(
    props?.steerageProvider?.fax?.toString() ?? null,
  )

  const hasIncompleteContactNumber =
    isIncompletePhoneNumber(phoneValue) || isIncompletePhoneNumber(faxValue)

  const isInputDirty = (name: string) => {
    const boolean = formik.getFieldMeta(name).value === formik.getFieldMeta(name).initialValue
    return !boolean
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: props?.steerageProviderCreateForm ? '' : props?.steerageProvider?.first_name,
      lastName: props?.steerageProviderCreateForm ? '' : props?.steerageProvider?.last_name,
      npi: props?.steerageProviderCreateForm ? '' : props?.steerageProvider?.npi,
      specialtyList: props?.steerageProviderCreateForm
        ? ''
        : props?.steerageProvider?.specialty_list?.join(', '),
      practiceName: props?.steerageProviderCreateForm
        ? ''
        : props?.steerageProvider?.care_organization_name,
      addressLine1: props?.steerageProviderCreateForm
        ? ''
        : props?.steerageProvider?.address_line_1,
      addressLine2: props?.steerageProviderCreateForm
        ? ''
        : props?.steerageProvider?.address_line_2,
      city: props?.steerageProviderCreateForm ? '' : props?.steerageProvider?.city,
      zipCode: props?.steerageProviderCreateForm ? '' : props?.steerageProvider?.zip_code,
      reasonForException: props?.steerageProviderCreateForm
        ? ''
        : props?.steerageProvider?.reason_for_exception,
      tins: props?.steerageProviderCreateForm
        ? ''
        : props?.steerageProvider?.stored_tins?.join(', ') || '',
      organizationNpis: props?.steerageProviderCreateForm
        ? ''
        : props?.steerageProvider?.stored_organization_npis?.join(', ') || '',
    },
    onSubmit: values => {
      const tins =
        values['tins'] == '' ? [] : values['tins']?.split(/[,\s]+/).filter(tin => tin.trim() !== '')
      const organization_npis =
        values['organizationNpis'] == ''
          ? []
          : values['organizationNpis']?.split(/[,\s]+/).filter(npi => npi.trim() !== '')

      // Snake_case payload shared by the create and edit paths (generated types from
      // @fireflyhealth/api).
      const payload = {
        first_name: values['firstName']?.trim() || null,
        last_name: values['lastName']?.trim() || null,
        npi: values['npi'] != null ? String(values['npi']) : null,
        specialty_list: values['specialtyList'] == '' ? [] : values['specialtyList']?.split(/[,]+/),
        care_organization_name: values['practiceName']?.trim() ?? null,
        address_line_1: values['addressLine1'] || null,
        address_line_2: values['addressLine2'] || null,
        city: values['city'] || null,
        state_id: selectedState?.id ?? null,
        zip_code: values['zipCode'] != null ? String(values['zipCode']) : null,
        phone: phoneValue || null,
        fax: faxValue || null,
        reason_for_exception: (values['reasonForException'] as ReasonForException) || null,
        is_manually_edited: true,
        tins,
        organization_npis,
      }

      if (props?.steerageProviderCreateForm) {
        // calling the create api for adding provider to the steerage
        handleAddProviderToSteerage(
          {
            steerage_id: props.steerage.id,
            recommendation_status: 'NEUTRAL',
            data_source: SteerageProviderDataSource.Manual,
            ...payload,
          },
          {
            onSuccess: () => {
              formik.resetForm()
              setSelectedState(null)
              // hide the form after saving the provider
              props?.onCancel()

              // invalidate queries for refetching the providers
              fireAndForget('[Steerage] refresh after adding provider', async () => {
                await client.cancelQueries({
                  queryKey: referralSteerage.retrieveQueryKey({ id: props?.steerage?.id ?? 0 }),
                })
                await client.invalidateQueries({
                  queryKey: referralSteerage.retrieveQueryKey({ id: props?.steerage?.id ?? 0 }),
                })
                await client.cancelQueries({
                  queryKey: referralSteerageProviders.listQueryKey({
                    steerage_id: props?.steerage?.id ?? 0,
                  }),
                })
                await client.invalidateQueries({
                  queryKey: referralSteerageProviders.listQueryKey({
                    steerage_id: props?.steerage?.id ?? 0,
                  }),
                })
              })
            },
          },
        )
      } else {
        const editPayload: EditSteerageProviderPayload = {
          ...payload,
          steerage_id: props.steerage.id,
          id: props.steerageProvider?.id ?? 0,
        }

        // If the phone or fax number value is changed then
        // mark the contact details as not verified
        if (
          phoneValue != props?.steerageProvider?.phone ||
          faxValue != props?.steerageProvider?.fax
        ) {
          editPayload.has_verified_contact_details = false
        }

        if (isInputDirty('npi') || isInputDirty('practiceName') || isInputDirty('zipCode')) {
          editPayload.network_partner_agreement_type = null
        }

        // calling the update provider api
        handleEditSteerageProvider(editPayload, {
          onSuccess: () => {
            // hide the form after saving the provider
            props?.onCancel()
            // invalidate queries for refetching the providers
            fireAndForget('[Steerage] refresh after updating provider', async () => {
              await client.cancelQueries({
                queryKey: referralSteerageProviders.listQueryKey({
                  steerage_id: props?.steerage?.id ?? 0,
                }),
              })
              await client.invalidateQueries({
                queryKey: referralSteerageProviders.listQueryKey({
                  steerage_id: props?.steerage?.id ?? 0,
                }),
              })
            })
          },
        })
      }
    },
  })

  const isProviderFormComplete = (): boolean => {
    // Don't validate if provider is added via search
    if (props?.steerageProvider?.data_source == SteerageProviderDataSource.Ribbon) {
      return true
    }

    // validate the provider form
    // Reason for exception is required
    if (!formik.values.reasonForException && props?.steerageProviderCreateForm) {
      return false
    }

    // For exceptions with practice name, either TIN or NPI2 is required
    if (
      formik.values.practiceName &&
      !formik.values.tins?.trim() &&
      !formik.values.organizationNpis?.trim()
    ) {
      return false
    }

    // Either FirstName, LastName and NPI is required or PracticeName is required
    if (
      (formik.values.firstName && formik.values.lastName && formik.values.npi) ||
      formik.values.practiceName
    ) {
      return true
    }
    return false
  }
  const isFormValid = isProviderFormComplete()

  const getFormTitle = (steerage: Steerage) => {
    // We need to show the type of steerage
    if (steerage.is_effectively_referral_and_waiver) return 'Care Key Only'
    if (steerage.is_effectively_referral_only) return 'Referral Only'
    if (steerage.is_effectively_waiver_only) return 'Care Key Only'
    return ''
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box>
            {props.steerageProviderCreateForm ? (
              <Typography style={{ fontWeight: 'bold' }}>Add Provider</Typography>
            ) : (
              <Typography style={{ fontWeight: 'bold' }}>
                Edit Provider({getFormTitle(props?.steerage)})
              </Typography>
            )}
          </Box>
          <br />
          <Grid container spacing={6}>
            <EditField
              label="First Name"
              type="input"
              name="firstName"
              onChange={formik.handleChange}
              value={formik.values.firstName}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <EditField
              label="Last Name"
              type="input"
              name="lastName"
              onChange={formik.handleChange}
              value={formik.values.lastName}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid container spacing={6}>
            <EditField
              label="NPI"
              type="number"
              name="npi"
              onChange={formik.handleChange}
              value={formik.values.npi}
              disabled={
                !props?.steerageProviderCreateForm &&
                props?.steerageProvider?.source_identifier != null
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <EditField
              label="Specialty"
              type="input"
              name="specialtyList"
              onChange={formik.handleChange}
              value={formik.values.specialtyList}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid container spacing={6}>
            <EditField
              label="Practice Name"
              type="input"
              name="practiceName"
              onChange={formik.handleChange}
              value={formik.values.practiceName}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <EditField
              label="TINs (Tax Identification Numbers)"
              type="input"
              name="tins"
              onChange={formik.handleChange}
              value={formik.values.tins}
              helperText="Enter multiple TINs separated by commas"
              required={!!formik.values.practiceName}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid container spacing={6}>
            <EditField
              label="Organization NPIs"
              type="input"
              name="organizationNpis"
              onChange={formik.handleChange}
              value={formik.values.organizationNpis}
              helperText="Enter multiple Organization NPIs separated by commas"
              required={!!formik.values.practiceName}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <EditField
              label="Address Line 1"
              type="input"
              name="addressLine1"
              onChange={formik.handleChange}
              value={formik.values.addressLine1}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid container spacing={6}>
            <EditField
              label="Address Line 2"
              type="input"
              name="addressLine2"
              onChange={formik.handleChange}
              value={formik.values.addressLine2}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <EditField
              label="City"
              type="input"
              name="city"
              onChange={formik.handleChange}
              value={formik.values.city}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid container spacing={6}>
            <Grid size={6}>
              <Autocomplete
                value={selectedState}
                onChange={setSearchTermsForState}
                inputValue={stateSearchString}
                onInputChange={(event, newSearchString) => {
                  setStateSearchString(newSearchString)
                }}
                id="state"
                autoComplete={true}
                size="small"
                forcePopupIcon={false}
                options={states || []}
                getOptionLabel={option => `${option.abbreviation} - ${option.name}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={({ key: _, ...props }, state: StatesListResponse[number]) => (
                  <li {...props} key={state.id}>
                    {state.abbreviation}
                    {' - '}
                    {state.name}
                  </li>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="State"
                    variant="standard"
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params?.slotProps.input,
                        startAdornment: <InputAdornment position="start"></InputAdornment>,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <EditField
              label="Zip Code"
              type="input"
              name="zipCode"
              onChange={formik.handleChange}
              value={formik.values.zipCode}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 5 } }}
            />
          </Grid>
          <Grid container spacing={6}>
            <Grid size={6}>
              <PhoneNumberField
                name="phone"
                label="Phone"
                size="small"
                variant="standard"
                value={phoneValue}
                onChange={setPhoneValue}
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <PhoneNumberField
                name="fax"
                label="Fax"
                size="small"
                variant="standard"
                value={faxValue}
                onChange={setFaxValue}
                fullWidth
              />
            </Grid>
          </Grid>
          {props?.steerageProviderCreateForm ? (
            <Grid container spacing={6}>
              <EditField
                label="Reason for Exception"
                select
                onChange={formik.handleChange}
                value={formik.values.reasonForException}
                name="reasonForException"
                slotProps={{ inputLabel: { shrink: true }, select: { native: true } }}
              >
                <option value="">None</option>
                {props?.reasonForExceptionList &&
                  props?.reasonForExceptionList?.length > 0 &&
                  props?.reasonForExceptionList?.map(reasonForException => (
                    <option key={reasonForException[0]} value={reasonForException[0]}>
                      {reasonForException[1]}
                    </option>
                  ))}
              </EditField>
            </Grid>
          ) : (
            <></>
          )}
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid size={8}>
              <Typography sx={{ fontWeight: 'medium', fontSize: '14px' }}>
                *Phone/Fax number update will reflect in the provider directory
              </Typography>
            </Grid>
            <Grid style={{ display: 'flex' }} size={4} sx={{ justifyContent: 'flex-end' }}>
              {props.steerageProviderCreateForm ? (
                <Box>
                  <Button onClick={props?.onCancel} color="primary" style={{ fontSize: '14px' }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => formik.handleSubmit()}
                    color="primary"
                    variant="contained"
                    disabled={!isFormValid || hasIncompleteContactNumber}
                    style={{ fontSize: '14px', borderRadius: '28px' }}
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                <>
                  <Button onClick={props?.onCancel} color="primary" style={{ fontSize: '14px' }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => formik.handleSubmit()}
                    color="primary"
                    variant="contained"
                    disabled={!isFormValid || hasIncompleteContactNumber}
                    style={{ fontSize: '14px', borderRadius: '28px' }}
                  >
                    Save
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <br />
      <Divider />
      <br />
    </Box>
  )
}

export default SteerageProviderForm
